import { useOpeningHours } from '@/features/opening-hours/hooks/useOpeningHours';
import { useI18nStore, useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { OpeningHours, ServiceType } from '@restaurant/api-contracts';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';

export type OpenStatus = { isOpen: boolean; label: string };

/** Locale-aware label builders + the day-name locale, passed in so the pure
 *  status function stays free of the i18n store. */
export type OpenStatusFormat = {
  openUntil: (time: string) => string;
  closedOpensAt: (time: string) => string;
  closedOpensDay: (day: string, time: string) => string;
  closed: string;
  /** BCP-47 locale for the abbreviated next-open weekday name. */
  locale: string;
};

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m ?? 0);
}

/**
 * Glanceable open/closed for a given `service`, evaluated in the restaurant's
 * own timezone. Defaults to the BASE (general) hours. When a non-BASE service
 * (e.g. RESERVATION) has no rows of its own it falls back to the BASE hours,
 * mirroring the backend's "inherit base hours unless overridden" rule. Weekly
 * hours only - holiday exceptions aren't loaded here, so this is an at-a-glance
 * indicator, not the authoritative gate the backend enforces. Returns null when
 * there are no applicable hours.
 */
export function computeOpenStatus(
  hours: OpeningHours[],
  timezone: string,
  fmt: OpenStatusFormat,
  service: ServiceType = 'BASE',
  /** The instant to evaluate against. Passed in (not read from the clock here)
   *  so the caller controls WHEN it recomputes — and so this stays pure. */
  nowMs: number = Date.now(),
): OpenStatus | null {
  const forService = hours.filter((h) => h.service === service);
  // Non-BASE services with no configured rows inherit the BASE weekly hours.
  const dineIn = forService.length > 0 ? forService : hours.filter((h) => h.service === 'BASE');
  if (dineIn.length === 0) return null;

  const now = DateTime.fromMillis(nowMs).setZone(timezone).setLocale(fmt.locale);
  const nowMin = now.hour * 60 + now.minute;
  const todayDow = now.weekday % 7; // luxon 1=Mon..7=Sun → schema 0=Sun..6=Sat

  const today = dineIn.find((h) => h.dayOfWeek === todayDow && h.isEnabled);
  if (today) {
    for (const s of today.shifts) {
      const open = minutesOf(s.open);
      const close = minutesOf(s.close);
      const crossesMidnight = close <= open;
      const inShift = crossesMidnight
        ? nowMin >= open || nowMin < close
        : nowMin >= open && nowMin < close;
      if (inShift) return { isOpen: true, label: fmt.openUntil(s.close) };
    }
    const nextToday = today.shifts
      .map((s) => s.open)
      .filter((o) => minutesOf(o) > nowMin)
      .sort((a, b) => minutesOf(a) - minutesOf(b))[0];
    if (nextToday) return { isOpen: false, label: fmt.closedOpensAt(nextToday) };
  }

  // Nothing left today - scan forward for the next open day this week.
  for (let i = 1; i <= 7; i++) {
    const dow = (todayDow + i) % 7;
    const day = dineIn.find((h) => h.dayOfWeek === dow && h.isEnabled && h.shifts.length > 0);
    if (day) {
      const first = [...day.shifts].sort((a, b) => minutesOf(a.open) - minutesOf(b.open))[0];
      const dayName = now.plus({ days: i }).toFormat('ccc');
      return { isOpen: false, label: fmt.closedOpensDay(dayName, first.open) };
    }
  }
  return { isOpen: false, label: fmt.closed };
}

/**
 * Re-renders once per minute, on the minute. The status below is a function of
 * the CURRENT time, but its inputs (the hours query) barely change — without a
 * tick a tab left open since before opening keeps claiming "Closed · opens
 * 11:30" at 11:53. Aligning to the minute boundary makes the label flip at
 * 11:30 rather than up to a minute late.
 */
function useMinuteNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    const timeout = setTimeout(() => {
      setNow(Date.now());
      interval = setInterval(() => setNow(Date.now()), 60_000);
    }, msToNextMinute);
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);
  return now;
}

/**
 * The active restaurant's live open/closed status for a `service` (default
 * BASE), in its own timezone. Shared by the Settings profile card, the app
 * sidebar (BASE) and the reservation pause card (RESERVATION). Null until hours
 * load / when none set. A non-BASE service with no rows inherits BASE hours.
 * Recomputed every minute so it stays true in a long-lived tab.
 */
export function useRestaurantOpenStatus(service: ServiceType = 'BASE'): OpenStatus | null {
  const t = useT();
  const locale = useI18nStore((s) => s.locale);
  const timezone = useRestaurantStore((s) => s.restaurantConfig?.timezone);
  const { data: openingHours } = useOpeningHours();
  const nowMs = useMinuteNow();
  return useMemo(
    () =>
      openingHours
        ? computeOpenStatus(
            openingHours,
            timezone ?? 'UTC',
            {
              openUntil: t.openingHours.statusOpenUntil,
              closedOpensAt: t.openingHours.statusClosedOpensAt,
              closedOpensDay: t.openingHours.statusClosedOpensDay,
              closed: t.openingHours.closedBadge,
              locale,
            },
            service,
            nowMs,
          )
        : null,
    [openingHours, timezone, t, locale, service, nowMs],
  );
}
